import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationContext";
import { NotificationBell } from "@/components/NotificationBell";
import { Button } from "@/components/ui/button";
import { Home, Upload, MessageSquare, User, Shield, Briefcase, LogOut, Bookmark } from "lucide-react";

const desktopLink = ({ isActive }) =>
  `hidden sm:inline-flex px-3 py-1.5 text-sm ${isActive ? "text-[#1E3F33] font-semibold" : "text-[#68706B] hover:text-[#1A1E1C]"}`;

const mobileLink = ({ isActive }) =>
  `flex flex-col items-center py-2 text-xs ${isActive ? "text-[#1E3F33]" : "text-[#68706B]"}`;

function DesktopNav({ user, messages, onLogout }) {
  return (
    <>
      <NavLink to="/feed" className={desktopLink} data-testid="nav-feed">Feed</NavLink>
      {user.role === "candidate" && (
        <NavLink to="/upload" className={desktopLink} data-testid="nav-upload">Upload</NavLink>
      )}
      <NavLink to="/inbox" className={({ isActive }) => `${desktopLink({ isActive })} items-center gap-1.5`} data-testid="nav-inbox">
        Inbox
        {messages > 0 && <span className="min-w-4 h-4 px-1 bg-[#D96C5B] text-white text-[10px] font-bold flex items-center justify-center" data-testid="nav-inbox-badge">{messages > 9 ? "9+" : messages}</span>}
      </NavLink>
      {(user.role === "hr" || user.role === "owner") && (
        <NavLink to="/shortlist" className={desktopLink} data-testid="nav-shortlist">Shortlist</NavLink>
      )}
      <NavLink to="/profile" className={({ isActive }) => `px-3 py-1.5 text-sm ${isActive ? "text-[#1E3F33] font-semibold" : "text-[#68706B] hover:text-[#1A1E1C]"}`} data-testid="nav-profile">Profile</NavLink>
      {user.role === "admin" && (
        <NavLink to="/admin" className={({ isActive }) => `hidden sm:inline-flex px-3 py-1.5 text-sm ${isActive ? "text-[#D96C5B] font-semibold" : "text-[#68706B] hover:text-[#D96C5B]"}`} data-testid="nav-admin">Admin</NavLink>
      )}
      {user.role === "owner" && (
        <NavLink to="/owner" className={desktopLink} data-testid="nav-owner">Company</NavLink>
      )}
      <NotificationBell />
      <Button onClick={onLogout} variant="ghost" size="sm" data-testid="btn-logout" className="rounded-none">
        <LogOut className="w-4 h-4" />
      </Button>
    </>
  );
}

function MobileNav({ user, messages }) {
  return (
    <nav className="sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-[#E2E4E2] grid grid-cols-5">
      <NavLink to="/feed" className={mobileLink} data-testid="mnav-feed"><Home className="w-5 h-5" />Feed</NavLink>
      {user.role === "candidate" ? (
        <NavLink to="/upload" className={mobileLink} data-testid="mnav-upload"><Upload className="w-5 h-5" />Upload</NavLink>
      ) : (user.role === "hr" || user.role === "owner") ? (
        <NavLink to="/shortlist" className={mobileLink} data-testid="mnav-shortlist"><Bookmark className="w-5 h-5" />Saved</NavLink>
      ) : (
        <span className="flex flex-col items-center py-2 text-xs text-[#C9CCC9]"><Briefcase className="w-5 h-5" />Hire</span>
      )}
      <NavLink to="/inbox" className={mobileLink} data-testid="mnav-inbox">
        <span className="relative">
          <MessageSquare className="w-5 h-5" />
          {messages > 0 && <span className="absolute -top-1 -right-2 min-w-4 h-4 px-0.5 bg-[#D96C5B] text-white text-[9px] font-bold flex items-center justify-center">{messages > 9 ? "9+" : messages}</span>}
        </span>
        Inbox
      </NavLink>
      <NavLink to="/profile" className={mobileLink} data-testid="mnav-profile"><User className="w-5 h-5" />Profile</NavLink>
      {user.role === "admin" ? (
        <NavLink to="/admin" className={({ isActive }) => `flex flex-col items-center py-2 text-xs ${isActive ? "text-[#D96C5B]" : "text-[#68706B]"}`} data-testid="mnav-admin"><Shield className="w-5 h-5" />Admin</NavLink>
      ) : user.role === "owner" ? (
        <NavLink to="/owner" className={mobileLink} data-testid="mnav-owner"><Briefcase className="w-5 h-5" />Team</NavLink>
      ) : (
        <span />
      )}
    </nav>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { counts } = useNotifications() || { counts: { messages: 0 } };
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#1A1E1C] flex flex-col">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-[#E2E4E2]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to={user ? "/feed" : "/"} className="flex items-center gap-2" data-testid="brand-link">
            <div className="w-7 h-7 bg-[#1E3F33] flex items-center justify-center">
              <span className="text-white font-bold text-sm">V</span>
            </div>
            <span className="font-bold text-lg tracking-tight">VisualCV</span>
          </Link>
          <nav className="flex items-center gap-1">
            {user ? (
              <DesktopNav user={user} messages={counts.messages} onLogout={onLogout} />
            ) : (
              <>
                <Link to="/login"><Button variant="ghost" className="rounded-none" data-testid="btn-login-nav">Sign in</Button></Link>
                <Link to="/register"><Button className="rounded-none bg-[#1E3F33] hover:bg-[#152C24]" data-testid="btn-signup-nav">Get started</Button></Link>
              </>
            )}
          </nav>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      {user && <MobileNav user={user} messages={counts.messages} />}
      {user && <div className="h-16 sm:hidden" />}
    </div>
  );
}
